import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { FaSearch, FaTrophy } from "react-icons/fa";
import { MdCardGiftcard } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const BASE_URL = "https://api.dreamtoday.in";

/* ── NO IMAGE PLACEHOLDER ── */
const NoImage = ({ name }) => (
  <div className="w-full h-60 flex flex-col items-center justify-center
    bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100">
    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-300 to-amber-400
      flex items-center justify-center shadow-md mb-3">
      <FaTrophy className="text-white text-2xl drop-shadow" />
    </div>
    <p className="text-xs text-yellow-700 font-medium text-center px-4 line-clamp-2">{name}</p>
  </div>
);

/* ── SKELETON CARD ── */
const SkeletonCard = () => (
  <div className="bg-white rounded-3xl overflow-hidden shadow-lg border border-yellow-200 animate-pulse">
    <div className="w-full h-60 bg-yellow-100" />
    <div className="p-6">
      <div className="h-5 bg-yellow-100 rounded mb-2 w-3/4" />
      <div className="h-4 bg-yellow-50 rounded mb-5 w-1/2" />
      <div className="h-11 bg-yellow-100 rounded-xl" />
    </div>
  </div>
);

/* ── EMPTY STATE ── */
const EmptyState = ({ search }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col items-center justify-center py-24 px-4"
  >
    <div className="relative mb-6">
      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-yellow-100 to-amber-200
        flex items-center justify-center shadow-lg border border-yellow-300">
        <MdCardGiftcard className="text-yellow-500 text-5xl" />
      </div>
      <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-amber-400
        flex items-center justify-center shadow-md">
        <FaSearch className="text-white text-xs" />
      </div>
    </div>
    <h3 className="text-xl font-bold text-yellow-800 mb-2">
      {search ? "No results found" : "No Prizes Available"}
    </h3>
    <p className="text-gray-500 text-sm text-center max-w-xs">
      {search
        ? `We couldn't find any prizes matching "${search}". Try a different keyword.`
        : "Prizes will appear here once campaigns are created."}
    </p>
  </motion.div>
);

const Prizes = () => {
  const [search, setSearch] = useState("");
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/getprizes`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setPrizes(d.prizes);
        else setError(d.message || "Failed to load prizes.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const filteredPrizes = useMemo(() => {
    return prizes.filter((p) =>
      p.prizeName.toLowerCase().includes(search.toLowerCase()) ||
      p.campaignTitle.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, prizes]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4">

        {/* HERO */}
        <div className="max-w-6xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4
            bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800
            bg-clip-text text-transparent">
            Explore Luxury Prizes
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover high-end rewards across our premium campaigns.
          </p>
        </div>

        {/* SEARCH */}
        <div className="max-w-3xl mx-auto mb-14">
          <div className="flex items-center bg-white border border-yellow-300 rounded-2xl px-5 py-4 shadow-sm">
            <FaSearch className="text-yellow-700 mr-3 shrink-0" />
            <input
              type="text"
              placeholder="Search prizes..."
              className="w-full outline-none text-gray-700"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="ml-2 text-gray-400 hover:text-gray-600 text-lg leading-none"
              >×</button>
            )}
          </div>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="max-w-7xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-24"
          >
            <div className="w-20 h-20 rounded-full bg-red-50 border border-red-200
              flex items-center justify-center mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <p className="text-red-500 font-semibold text-lg mb-1">Something went wrong</p>
            <p className="text-gray-400 text-sm">{error}</p>
          </motion.div>
        )}

        {/* PRIZES GRID */}
        {!loading && !error && filteredPrizes.length > 0 && (
          <div className="max-w-7xl mx-auto grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {filteredPrizes.map((prize, index) => (
              <motion.div
                key={prize.prizeId}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -10 }}
                className="relative bg-white rounded-3xl overflow-hidden shadow-lg border border-yellow-200 group"
              >
                {/* IMAGE */}
                <div className="relative overflow-hidden">
                  {prize.prizeImage ? (
                    <img
                      src={`${BASE_URL}${prize.prizeImage}`}
                      alt={prize.prizeName}
                      className="w-full h-60 object-cover group-hover:scale-105 transition duration-500"
                    />
                  ) : (
                    <NoImage name={prize.prizeName} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-60" />
                </div>

                {/* CONTENT */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {prize.prizeName}
                  </h3>
                  <p className="text-sm text-gray-500 mb-5">
                    Part of <span className="text-yellow-700 font-medium">{prize.campaignTitle}</span>
                  </p>
                  <button
                    onClick={() => navigate(`/campaigns/${prize.campaignId}`)}
                    className="w-full py-3 rounded-xl text-white font-semibold
                      bg-gradient-to-r from-yellow-600 to-yellow-800 hover:scale-105 transition"
                  >
                    View Campaign
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && !error && filteredPrizes.length === 0 && (
          <EmptyState search={search} />
        )}

      </div>
    </>
  );
};

export default Prizes;